import { useRef, useState } from 'react';
import { Editor } from '@revolist/react-datagrid';
import type { ColumnRegular, EditorCtr, EditorType } from '@revolist/react-datagrid';
import { Box, ListSubheader, MenuItem, Select, ThemeProvider, createTheme } from '@mui/material';
import type { Theme } from '@mui/material';

export type SelectOption = string | { label: string; value: string; group?: string };

export const toSelectOption = (option: SelectOption) =>
  typeof option === 'string' ? { label: option, value: option } : option;

/** Label to show for a stored value — the value itself when it is not in the list. */
export const getSelectOptionLabel = (options: readonly SelectOption[], value: unknown) => {
  const stored = (value ?? '') as string;
  return options.map(toSelectOption).find((option) => option.value === stored)?.label ?? stored;
};

let sharedTheme: Theme | null = null;
const fallbackTheme = createTheme();

export const setEditorTheme = (theme: Theme) => {
  sharedTheme = theme;
};

type SelectEditorProps = EditorType & { options: readonly SelectOption[] };

export const FIELD_HEIGHT = 37;
/** RHFSelect's menu: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP */
const MENU_MAX_HEIGHT = 48 * 4.5 + 8;

const SelectCellEditor = ({ column, val, save, close, options }: SelectEditorProps) => {
  const initial = (val ?? column?.value ?? '') as string;
  const [open, setOpen] = useState(true);

  const savedRef = useRef(false);
  const items = options.map(toSelectOption);

  return (
    <ThemeProvider theme={sharedTheme ?? fallbackTheme}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          width: '100%',
          px: 2,
          boxSizing: 'border-box',
        }}
      >
        <Select
          open={open}
          autoFocus
          fullWidth
          size="small"
          value={items.some((item) => item.value === initial) ? initial : ''}
          onChange={(event) => {
            savedRef.current = true;
            save(event.target.value);
          }}
          onClose={() => {
            setOpen(false);
            if (!savedRef.current) {
              close();
            }
          }}
          sx={{
            height: FIELD_HEIGHT,
            fontSize: (theme) => theme.typography.body2.fontSize,
            backgroundColor: (theme) => theme.palette.background.default,
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              minHeight: 'unset',
              py: 0,
            },
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: MENU_MAX_HEIGHT,
                backgroundColor: (sharedTheme ?? fallbackTheme).palette.background.default,
              },
            },
          }}
        >
          {items.flatMap((item, index) => {
            const startsGroup = !!item.group && item.group !== items[index - 1]?.group;

            return [
              ...(startsGroup
                ? [
                    <ListSubheader key={`group-${item.group}`} sx={{ fontSize: 'inherit' }}>
                      {item.group}
                    </ListSubheader>,
                  ]
                : []),
              <MenuItem key={item.value} value={item.value} sx={{ fontSize: 'inherit' }}>
                {item.label}
              </MenuItem>,
            ];
          })}
        </Select>
      </Box>
    </ThemeProvider>
  );
};

const editorOptions = new WeakMap<EditorCtr, readonly SelectOption[]>();

export const getEditorOptions = (
  editor: ColumnRegular['editor']
): readonly SelectOption[] | undefined =>
  typeof editor === 'string' || !editor ? undefined : editorOptions.get(editor);

export const createSelectEditor = (options: readonly SelectOption[]) => {
  const editor = Editor((props: EditorType) => <SelectCellEditor {...props} options={options} />);
  editorOptions.set(editor, options);

  return editor;
};
